import { styled, Button, Typography, Chip } from '@ori-ui/mui';
import { LogLevel, getLogger } from '@ori/checkout-utils';
import useTestId from '@ori/testid-generator';
import { useContext, useEffect, useState } from 'react';

import { APP_NAME } from '../constants';
import { stateContext } from '../contexts';
import { usePackageSelectionLogic } from '../hooks';
import { useTranslations } from '../hooks/useTranslations';
import type { Package } from '../types';
import { ModalContent } from './ModalContent';
import { PackagePicture } from './PackagePicture';
import { PackagesLoader } from './PackagesLoader';

const logDebug = getLogger(APP_NAME, LogLevel.Debug);

const Filters = styled('section')({
  width: '100%',
});

const Packages = styled('div')(({ theme }) => ({
  display: 'grid',
  overflow: 'auto',
  rowGap: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  color: theme.palette.grey[200], // needed because it is inherited by button
}));

const PackageButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  justifyContent: 'stretch',
  padding: theme.spacing(2, 3),
}));

const PackageLabel = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  textTransform: 'none',
  color: theme.palette.text.primary,
}));

export function PackageSelection() {
  const { getTestIdProps } = useTestId();
  const { setPackage, selectedPackage } = useContext(stateContext);
  const [filter, setFilter] = useState(0);
  const { formatMessage } = useTranslations();

  const { packages, categories, isLoading } = usePackageSelectionLogic(filter);

  logDebug('PackageSelection selectedPackage: ', selectedPackage);

  useEffect(() => {
    if (categories.length === 1) {
      const categoryTypeId = categories[0]?.typeId ?? 0;
      setFilter(categoryTypeId);
    }
  }, [categories]);

  const onClickSetPackage = (autoshipPackage: Package) => () => setPackage(autoshipPackage);

  const onClickSetFilter = (categoryFilter: number) => () => setFilter(categoryFilter);

  return (
    <ModalContent dataId="package-selection">
      {isLoading ? (
        <PackagesLoader />
      ) : (
        <>
          <Filters>
            {categories.length > 1 && (
              <Chip
                clickable
                label={formatMessage('All')}
                size="small"
                sx={{ marginRight: 2 }}
                variant={filter > 0 ? 'outlined' : undefined}
                onClick={onClickSetFilter(0)}
              />
            )}
            {categories.map((category) => (
              <Chip
                key={category.typeId}
                clickable
                label={category.description}
                size="small"
                variant={filter === category.typeId ? undefined : 'outlined'}
                sx={{ marginRight: 2 }}
                {...getTestIdProps(`${category.category}`)}
                onClick={onClickSetFilter(category.typeId)}
              />
            ))}
          </Filters>

          <Packages {...getTestIdProps('packages')}>
            {packages.map((item: Package) => (
              <PackageButton
                key={item.packageId}
                disableRipple
                fullWidth
                color="inherit"
                size="small"
                startIcon={
                  <PackagePicture
                    url={item.imageSrc}
                    alt={item.description}
                  />
                }
                sx={item.packageId === selectedPackage?.packageId ? { borderColor: 'text.primary' } : undefined}
                {...getTestIdProps(`package-${item.packageId}-button`)}
                type="button"
                variant="outlined"
                onClick={onClickSetPackage(item)}
              >
                <PackageLabel
                  variant="body1"
                  {...getTestIdProps('package-label')}
                >
                  {item.description}
                </PackageLabel>
              </PackageButton>
            ))}
          </Packages>
        </>
      )}
    </ModalContent>
  );
}
